import { useState, useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  allowedRoles: string[];
  loginPath?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  role: string | null;
  loading: boolean;
}

export function ProtectedRoute({ allowedRoles, loginPath = "/login" }: ProtectedRouteProps) {
  const [auth, setAuth] = useState<AuthState>({
    isAuthenticated: false,
    role: null,
    loading: true,
  });
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch("/api/check_auth.php");
        const data = await response.json();
        
        if (data.authenticated) {
          setAuth({
            isAuthenticated: true,
            role: data.role,
            loading: false,
          });
        } else {
          setAuth({
            isAuthenticated: false,
            role: null,
            loading: false,
          });
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        setAuth({
          isAuthenticated: false,
          role: null,
          loading: false,
        });
      }
    };

    checkAuth();
  }, []);

  if (auth.loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    // Redirect to login page, saving the current location
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  if (auth.role && !allowedRoles.includes(auth.role)) {
    // User is authenticated but doesn't have the required role
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50 px-4 text-center">
        <h1 className="text-3xl font-bold text-red-600 mb-4">Accesso Negato</h1>
        <p className="text-lg text-gray-700 mb-6">
          Non hai i permessi necessari per accedere a questa pagina.
        </p>
        <p className="text-sm text-gray-500">
          Ruolo attuale: <span className="font-mono font-bold">{auth.role}</span>
        </p>
        <button 
          onClick={() => window.history.back()}
          className="mt-8 px-6 py-2 bg-brand-primary text-white rounded hover:bg-brand-secondary transition-colors"
        >
          Torna Indietro
        </button>
      </div>
    );
  }

  return <Outlet />;
}
